import React, { useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../../providers/AuthProvider';
import Button from '../common/Button';
import FormInput from '../form-elements/FormInput';
import FormGroupErrorText from '../form-elements/FormGroupErrorText';
import styles from '../../styles/onboarding/OnboardingTwoFactorSetup.module.scss';

interface OnboardingTwoFactorSetupProps {
  onComplete: () => void;
}

const TwoFactorSetupSchema = Yup.object().shape({
  otp: Yup.string()
    .required('Please enter the verification code')
    .length(6, 'Verification code must be 6 digits'),
});

const OnboardingTwoFactorSetup: React.FC<OnboardingTwoFactorSetupProps> = ({ onComplete }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [setupData, setSetupData] = useState<{ qrCodeUrl: string; secretKey: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const { setup2FA, verify2FASetup } = useAuth();

  useEffect(() => {
    const initializeSetup = async () => {
      try {
        setIsInitializing(true);
        const data = await setup2FA();
        setSetupData(data);
      } catch (error: any) {
        setError(error.message || 'Failed to initialize 2FA setup');
      } finally {
        setIsInitializing(false);
      }
    };

    initializeSetup();
  }, [setup2FA]);

  const handleSubmit = async (values: { otp: string }) => {
    setIsLoading(true);
    setError(null);

    try {
      const success = await verify2FASetup(values.otp);
      if (success) {
        onComplete();
      } else {
        setError('Invalid verification code. Please try again.');
      }
    } catch (error: any) {
      setError(error.message || 'Failed to verify 2FA setup');
    } finally {
      setIsLoading(false);
    }
  };

  if (isInitializing) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
        <p className={styles.loadingText}>Setting up Two-Factor Authentication...</p>
      </div>
    );
  }

  if (error && !setupData) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.errorIcon}>⚠️</div>
        <h3 className={styles.errorTitle}>Setup Failed</h3>
        <p className={styles.errorMessage}>{error}</p>
        <Button
          text="Try Again"
          btnSize="md"
          onClick={() => window.location.reload()}
        />
      </div>
    );
  }

  return (
    <div className={styles.setupContainer}>
      <div className={styles.setupHeader}>
        <p className={styles.setupSubtitle}>
          Secure your account with two-factor authentication using your mobile device.
        </p>
      </div>

      <div className={styles.setupContent}>
        <div className={styles.instructionsSection}>
          <h3 className={styles.sectionTitle}>Step 1: Install an Authenticator App</h3>
          <p className={styles.sectionText}>
            Download and install an authenticator app like Google Authenticator, Authy, or 1Password on your mobile device.
          </p>
        </div>

        <div className={styles.qrSection}>
          <h3 className={styles.sectionTitle}>Step 2: Scan the QR Code</h3>
          <div className={styles.qrContainer}>
            {setupData?.qrCodeUrl && (
              <img 
                src={setupData.qrCodeUrl} 
                alt="QR Code for 2FA setup"
                className={styles.qrCode}
              />
            )}
          </div>
          <p className={styles.qrText}>
            Scan this QR code with your authenticator app to add your Liquid Credit account.
          </p>
          
          <div className={styles.secretKeySection}>
            <p className={styles.secretKeyLabel}>Manual entry code:</p>
            <div className={styles.secretKeyContainer}>
              <code className={styles.secretKey}>{setupData?.secretKey}</code>
            </div>
          </div>
        </div>

        <div className={styles.verificationSection}>
          <h3 className={styles.sectionTitle}>Step 3: Enter Verification Code</h3>
          <p className={styles.sectionText}>
            Enter the 6-digit code from your authenticator app to complete setup.
          </p>

          <Formik
            initialValues={{ otp: '' }}
            validationSchema={TwoFactorSetupSchema}
            onSubmit={handleSubmit}
          >
            {({ isValid, dirty }) => (
              <Form className={styles.verificationForm}>
                <FormInput
                  type="text"
                  name="otp"
                  placeholder="000000"
                  maxLength={6}
                  extraClassName="text-center"
                  labelText="Verification Code"
                  hideError
                />

                {error && <FormGroupErrorText text={error} />}

                <div className={styles.buttonGroup}>
                  <Button
                    type="submit"
                    btnSize="lg"
                    disabled={!isValid || !dirty || isLoading}
                    isLoading={isLoading}
                    isLoadingText="Verifying..."
                    text="Complete Setup"
                  />
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTwoFactorSetup;
